import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { VitalsSkeleton } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/vitalsService'
import MiniSparkline from './components/MiniSparkline'
import VitalsLogDialog from './components/VitalsLogDialog'
import { parseUtcDate } from '../../utils/dates'

const MetricCard = styled(Card)`
  cursor: pointer;
  transition: border-color 200ms ease-out, transform 200ms ease-out;
  &:hover {
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
`

const MetricHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
`

const MetricTitle = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  letter-spacing: -0.01em;
`

const Unit = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-left: 6px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const METRICS = [
  { metric: 'BP', label: 'Blood pressure', unit: 'mmHg', color: '#1F3A5F' },
  { metric: 'WEIGHT', label: 'Weight', unit: 'kg', color: '#6B9080' },
  { metric: 'SLEEP_HOURS', label: 'Sleep', unit: 'hrs', color: '#1F3A5F' },
  { metric: 'MOOD', label: 'Mood', unit: '/5', color: '#B97A2A' },
]

const moodEmoji = ['😢', '😕', '😐', '🙂', '😊']

function latestValue(entries, metric) {
  if (!entries?.length) return null
  const sorted = [...entries].sort((a, b) =>
    (a.takenAt || '').localeCompare(b.takenAt || ''),
  )
  return sorted[sorted.length - 1]
}

export default function VitalsPage() {
  const { currentProfile } = usePatient()
  const [byMetric, setByMetric] = useState({})
  const [loading, setLoading] = useState(true)
  const [logTarget, setLogTarget] = useState(null)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const to = new Date().toISOString()
      const from = parseUtcDate(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
      const results = await Promise.all(
        METRICS.map((m) => svc.list(currentProfile, m.metric, from, to)),
      )
      const next = {}
      METRICS.forEach((m, i) => {
        next[m.metric] = results[i] || []
      })
      setByMetric(next)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setByMetric({})
    refresh()
  }, [refresh])

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/care">Back to Care</BackLink>
        <div>
          <Pill>Care</Pill>
          <PageHeading style={{ marginTop: 12 }}>Vitals</PageHeading>
          <PageSubheading>Blood pressure, weight, sleep, and mood — last 14 days.</PageSubheading>
        </div>

        {loading && Object.keys(byMetric).length === 0 ? (
          <VitalsSkeleton />
        ) : (
          <Grid>
            {METRICS.map((m) => {
              const entries = byMetric[m.metric] || []
              const latest = latestValue(entries, m.metric)
              const values = entries
                .map((e) => (typeof e.value === 'number' ? e.value : null))
                .filter((v) => v != null)
              return (
                <MetricCard key={m.metric} onClick={() => setLogTarget(m.metric)}>
                  <MetricHeader>
                    <MetricTitle>{m.label}</MetricTitle>
                    <Pill>{entries.length} readings</Pill>
                  </MetricHeader>
                  {latest ? (
                    <>
                      <MetricValue>
                        {m.metric === 'MOOD'
                          ? moodEmoji[Math.max(0, Math.min(4, (latest.value || 1) - 1))]
                          : m.metric === 'BP'
                          ? latest.valueText
                          : latest.value}
                        <Unit>
                          {m.metric === 'MOOD'
                            ? ` ${latest.value}/5`
                            : ` ${m.unit}`}
                        </Unit>
                      </MetricValue>
                      <Muted style={{ marginTop: 4, fontSize: 12 }}>
                        Last:{' '}
                        {parseUtcDate(latest.takenAt).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Muted>
                      <div style={{ marginTop: 12 }}>
                        <MiniSparkline values={values} color={m.color} />
                      </div>
                    </>
                  ) : (
                    <Muted>No readings yet — tap to log.</Muted>
                  )}
                </MetricCard>
              )
            })}
          </Grid>
        )}
      </Stack>

      <VitalsLogDialog
        open={!!logTarget}
        metric={logTarget}
        onClose={() => setLogTarget(null)}
        onSubmit={async (body) => {
          await svc.record(currentProfile, body)
          await refresh()
        }}
      />
    </ContentMaxWidth>
  )
}
